/**
 * Demander une information manquante — chunk 9.AN.1.
 *
 * ## Où ce bouton apparaît, et où il n'apparaît pas
 *
 * Uniquement sur une cellule **`not_provided`** rattachée à un destinataire
 * assureur identifiable. Sur `not_offered`, l'assureur a déjà répondu — redemander
 * donnerait l'impression de n'avoir pas lu. Sur `needs_review`, la valeur existe et
 * c'est à NOUS de la vérifier, pas à l'assureur de la refournir.
 *
 * Le serveur refuse de toute façon ces cas ; l'interface ne propose simplement pas
 * un geste qui serait rejeté.
 *
 * ## Compact par nécessité
 *
 * Une matrice comparative porte des dizaines de cellules. Un bouton pleine largeur
 * par cellule la rendrait illisible : ici, un lien discret quand rien n'est demandé,
 * un état court quand une demande existe.
 */

import { useTranslation } from "react-i18next";

import {
  useCreateInsurerInformationRequest,
  type DemandeVars,
} from "../../../lib/queries/insurerInformationRequests";

export type DemandeExistante = {
  status: string;
  last_sent_at: string | null;
  sent_to: string | null;
};

export function RequestInformationCell({
  cellStatus,
  recipientId,
  fieldPath,
  demande,
}: {
  cellStatus: string;
  recipientId: string | null | undefined;
  fieldPath: string;
  demande: DemandeExistante | undefined;
}) {
  const { t } = useTranslation();
  const creer = useCreateInsurerInformationRequest();

  // Une demande déjà ouverte : on montre son état, jamais un second bouton.
  if (demande) {
    const clos = demande.status === "fulfilled" || demande.status === "waived";
    return (
      <span
        className={`mt-0.5 block text-[11px] ${clos ? "text-emerald-700" : "text-slate-500"}`}
        title={
          demande.sent_to ? t("information_request.sent_to", { email: demande.sent_to }) : undefined
        }
      >
        {t(`information_request.status.${demande.status}`, {
          defaultValue: t("information_request.status.open"),
        })}
      </span>
    );
  }

  // Sans destinataire identifiable, la demande n'aurait personne à qui partir.
  // On se tait plutôt que d'offrir un bouton qui échouerait.
  if (cellStatus !== "not_provided" || !recipientId) return null;

  const vars: DemandeVars = { recipient_id: recipientId, field_path: fieldPath };

  return (
    <button
      type="button"
      onClick={() => creer.mutate(vars)}
      disabled={creer.isPending}
      className="focus-visible:ring-brand-500 text-brand-700 mt-0.5 block touch-manipulation text-[11px] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-60"
    >
      {creer.isPending ? t("information_request.sending") : t("information_request.cta")}
    </button>
  );
}
